import { Field, Input } from '@base-ui/react';
import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import {
  autofillSurface,
  changedField,
  fieldBorder,
  focusRing,
  hoverable,
} from '../../interaction-states';
import { typographyVariantStyles } from '../../typography-variants';
import type { HelperState } from '../Helper/types';
import type { InputSize } from './types';

const sizeConfig: Record<
  InputSize,
  { padding: string; height: number; borderRadius: number; typography: 'body2' | 'body3' }
> = {
  sm: { padding: '6px 12px', height: 32, borderRadius: 8, typography: 'body3' },
  md: { padding: '8px 12px', height: 36, borderRadius: 12, typography: 'body3' },
  lg: { padding: '8px 12px', height: 40, borderRadius: 12, typography: 'body2' },
  xl: { padding: '12px 16px', height: 48, borderRadius: 12, typography: 'body2' },
};

function getBorderColor(theme: Theme, state: HelperState): string {
  switch (state) {
    case 'destructive':
      return theme.colors.error.border;
    case 'success':
      return theme.colors.success.border;
    default:
      return theme.colors.border.default;
  }
}

export const StyledRoot = styled(Field.Root)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: '100%',

  '&[data-disabled]': {
    opacity: 0.5,
    pointerEvents: 'none',
  },

  ...changedField(theme),
}));

export const StyledBox = styled('div', {
  shouldForwardProp: (prop) => !['$size', '$state'].includes(prop as string),
})<{ $size: InputSize; $state: HelperState }>(({ theme, $size, $state }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  boxSizing: 'border-box',
  padding: sizeConfig[$size].padding,
  height: sizeConfig[$size].height,
  borderRadius: sizeConfig[$size].borderRadius,
  backgroundColor: theme.colors.background.overlay,
  border: `1px solid ${fieldBorder(getBorderColor(theme, $state))}`,
  transition: theme.motion.hover,
  ...($state === 'default' && hoverable(theme, 'border')),
  ...focusRing(theme, { within: true }),
}));

export const StyledIconWrapper = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  color: theme.colors.text.primary,

  '& svg': {
    width: '100%',
    height: '100%',
  },
}));

export const StyledEndControlWrapper = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: theme.colors.text.primary,
}));

export const StyledInput = styled(Input, {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: InputSize }>(({ theme, $size }) => ({
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: theme.colors.text.primary,
  fontFamily: 'inherit',
  ...typographyVariantStyles[sizeConfig[$size].typography],

  '&::placeholder': {
    color: theme.colors.text.secondary,
  },

  '&[data-disabled]': {
    cursor: 'not-allowed',
  },

  ...autofillSurface(theme),
}));
